import React from 'react';

import Pill from 'carbon-react/lib/components/pill';

import { ExtendedLiveReportList } from '@futrli/views/processors/dashboards/dashboardsList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';
import { ResourceAccessLabel } from '@futrli/predict/sharing/components/resource-access-label/resource-access-label';

import { MyDashboardRowActions } from '../my-dashboard-row-actions/my-dashboard-row-actions';

export const ROW_HEIGHT_DEFAULT = 78;

const getStatusBadge = (
  dashboard: ExtendedLiveReportList,
  intl: ReturnType<typeof useIntl>,
  isResourceSharingEnabled: boolean
) => {
  const performanceDashboardBadge = dashboard.isPerformanceDashboard ? (
    <Pill colorVariant="information" size="S" pillRole="status" fill>
      {intl.formatMessage({
        id: 'predict.dashboards.status.performance_dashboard',
        defaultMessage: 'Performance dashboard'
      })}
    </Pill>
  ) : null;

  if (isResourceSharingEnabled) {
    const sharingBadge =
      dashboard.permission_info?.sharing_status === 'shared' ? (
        <Pill colorVariant="positive" size="S" fill>
          {intl.formatMessage({ id: 'common.shared', defaultMessage: 'Shared' })}
        </Pill>
      ) : (
        <Pill size="S" pillRole="status">
          {intl.formatMessage({ id: 'common.private', defaultMessage: 'Private' })}
        </Pill>
      );

    return (
      <span style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 4, whiteSpace: 'normal' }}>
        {sharingBadge}
        {performanceDashboardBadge}
      </span>
    );
  }

  if (performanceDashboardBadge) {
    return performanceDashboardBadge;
  }

  return dashboard.status === 'live' ? (
    <Pill colorVariant="positive" size="S" fill>
      {intl.formatMessage({ id: 'common.published', defaultMessage: 'Published' })}
    </Pill>
  ) : (
    <Pill size="S" pillRole="status">
      {intl.formatMessage({ id: 'common.status.draft', defaultMessage: 'Draft' })}
    </Pill>
  );
};

interface MyDashboardRowProps {
  dashboard: ExtendedLiveReportList;
  isResourceSharingEnabled?: boolean;
  onNameClick: (dashboardId: string) => void;
}

export const MyDashboardRow = ({ dashboard, isResourceSharingEnabled = false, onNameClick }: MyDashboardRowProps) => {
  const intl = useIntl();

  const createdDate = intl.formatDate(dashboard.created, { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <ListingPageRowLayout
      nameColumn={<ListingPageRowNameCell name={dashboard.name} onNameClick={() => onNameClick(dashboard.id)} />}
      metrics={
        <>
          <ListingPageRowMetricCell
            size={isResourceSharingEnabled ? 'statusExtraLarge' : 'statusLarge'}
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={getStatusBadge(dashboard, intl, isResourceSharingEnabled)}
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.access" defaultMessage="Access" />}
            value={
              isResourceSharingEnabled ? (
                <ResourceAccessLabel permission={dashboard.permission_info?.effective_permission} />
              ) : dashboard.actions.edit ? (
                <FormattedMessage id="common.edit" defaultMessage="Edit" />
              ) : (
                <FormattedMessage id="common.read_only" defaultMessage="Read only" />
              )
            }
          />
          <ListingPageRowMetricCell
            label={<FormattedMessage id="common.date_created" defaultMessage="Date created" />}
            value={createdDate}
          />
          <ListingPageRowMetricCell
            size="wide"
            label={<FormattedMessage id="common.owner" defaultMessage="Owner" />}
            value={
              dashboard.isOwner ? (
                <FormattedMessage id="common.owner.you" defaultMessage="You" />
              ) : (
                dashboard.ownerName || '—'
              )
            }
          />
        </>
      }
      actions={<MyDashboardRowActions dashboard={dashboard} />}
    />
  );
};
